import { keyframes } from '@emotion/react'
import { makeStyles } from 'tss-react/mui'
import { fundingColors as C } from '../../theme/fundingColors'
import {
  FUNDING_FORM_FIELD_HEIGHT,
  fundingFormFieldBox,
  fundingPrimaryButton,
} from '../../theme/fundingSharedStyles'
import { fundingFont, fundingType } from '../../theme/fundingTypography'

const skeletonShimmer = keyframes({
  '0%': { backgroundPosition: '100% 0' },
  '100%': { backgroundPosition: '-100% 0' },
})

export const useStyles = makeStyles()(theme => ({
  root: {
    boxSizing: 'border-box',
    width: '100%',
    display: 'flex',
    gap: 32,
    alignItems: 'flex-start',
    fontFamily: fundingFont,
    color: C.strongText,
    // Override MUI Typography theme (Poppins) inside Funding Settings.
    '& .MuiTypography-root': { fontFamily: fundingFont },
  },
  leftColumn: {
    flex: '1 1 0',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
  },
  rightColumn: {
    width: 308,
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
  },
  card: {
    boxSizing: 'border-box',
    width: '100%',
    backgroundColor: C.cardBg,
    borderRadius: 8,
    padding: 24,
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    width: '100%',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    minWidth: 0,
  },
  headerTitleGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    minWidth: 0,
  },
  iconBox: {
    width: 28,
    height: 28,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: C.iconBoxBg,
    borderRadius: 4,
    '& svg': { fontSize: 16, color: C.strongText },
  },
  statusIconError: {
    '& svg': { color: C.dangerDot },
  },
  statusIconOk: {
    '& svg': { color: C.successText },
  },
  /** Figma section step icon — muted circle-check, not red/green. */
  statusIconMuted: {
    '& svg': { color: C.sectionIconMuted },
  },
  cardTitle: {
    ...fundingType.cardTitle,
    color: C.strongText,
    margin: 0,
  },
  collapseButton: {
    flexShrink: 0,
    width: 24,
    height: 24,
    padding: 0,
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: C.mutedText,
    '& svg': { fontSize: 16 },
  },
  basicGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    columnGap: 16,
    rowGap: 16,
    width: '100%',
  },
  basicField: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    minWidth: 0,
  },
  outlinedField: {
    ...fundingFormFieldBox,
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 16px',
    border: `1px solid ${C.buttonBorder}`,
    borderRadius: 4,
    backgroundColor: C.cardBg,
  },
  floatField: {
    ...fundingFormFieldBox,
    position: 'relative' as const,
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 16px',
    border: `1px solid ${C.buttonBorder}`,
    borderRadius: 4,
    backgroundColor: C.cardBg,
    '&:focus-within': {
      borderColor: C.linkBlue,
    },
  },
  // Label sits on the border notch — keep symmetric padding so value text stays centered.
  floatFieldFloated: {},
  floatFieldError: {
    borderColor: C.errorText,
  },
  floatLabelInField: {
    position: 'absolute' as const,
    left: 16,
    top: '50%',
    transform: 'translateY(-50%)',
    margin: 0,
    pointerEvents: 'none' as const,
    transition: 'top 120ms ease, transform 120ms ease, font-size 120ms ease',
    ...fundingType.fieldLabel,
    fontSize: 14,
    color: C.mutedText,
    backgroundColor: C.cardBg,
    padding: '0 2px',
    maxWidth: 'calc(100% - 48px)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  floatLabelFloated: {
    top: 0,
    transform: 'translateY(-50%)',
    fontSize: 12,
    lineHeight: '16px',
  },
  input: {
    flex: '1 1 auto',
    minWidth: 0,
    height: '100%',
    border: 'none',
    outline: 'none',
    background: 'none',
    ...fundingType.body,
    lineHeight: '20px',
    color: C.darkText,
    '&::placeholder': { color: C.mutedText },
  },
  select: {
    flex: '1 1 auto',
    minWidth: 0,
    height: '100%',
    border: 'none',
    outline: 'none',
    background: 'none',
    ...fundingType.body,
    lineHeight: '20px',
    color: C.darkText,
    appearance: 'none' as const,
    cursor: 'pointer',
    '&:invalid': { color: C.mutedText },
  },
  inputInfoIcon: {
    flexShrink: 0,
    fontSize: 16,
    color: C.mutedText,
  },
  scaFloatField: {
    ...fundingFormFieldBox,
    alignItems: 'center',
  },
  scaRadioRow: {
    display: 'inline-flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 20,
    minHeight: 24,
    flex: '1 1 auto',
    minWidth: 0,
  },
  scaRadioOption: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    margin: 0,
    cursor: 'pointer',
    fontFamily: fundingFont,
    fontSize: 14,
    fontWeight: 400,
    lineHeight: '20px',
    color: C.darkText,
    '& input': {
      margin: 0,
      width: 16,
      height: 16,
      accentColor: C.linkBlue,
      flexShrink: 0,
    },
  },
  helperText: {
    padding: '4px 16px 0',
    ...fundingType.helper,
    color: C.mutedText,
  },
  howToFindLink: {
    display: 'inline-block',
    alignSelf: 'flex-start',
    margin: 0,
    padding: '4px 16px 0',
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    ...fundingType.linkHelper,
    color: C.linkBlue,
    textDecoration: 'none',
    textAlign: 'left',
    '&:hover': { textDecoration: 'underline' },
  },
  helperTextError: {
    padding: '4px 16px 0',
    ...fundingType.helper,
    color: theme.palette.error.main,
  },
  browseProgramsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginTop: 8,
    position: 'relative',
  },
  browseProgramsLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    border: 'none',
    background: 'none',
    padding: 0,
    cursor: 'pointer',
    ...fundingType.bodyMedium,
    color: C.linkBlue,
    textDecoration: 'none',
    '&:hover': { textDecoration: 'underline' },
    '&:disabled': { opacity: 0.5, cursor: 'not-allowed' },
  },
  browseProgramsMenu: {
    boxSizing: 'border-box',
    width: 420,
    maxWidth: 'min(420px, calc(100vw - 32px))',
    marginTop: 4,
    borderRadius: 8,
    border: `1px solid ${C.divider}`,
    boxShadow: '0px 8px 24px rgba(15, 23, 43, 0.12)',
    overflow: 'hidden',
  },
  browseProgramsSearchWrap: {
    padding: '12px 12px 8px',
    borderBottom: `1px solid ${C.divider}`,
  },
  browseProgramsSearch: {
    boxSizing: 'border-box',
    width: '100%',
    height: 36,
    padding: '8px 12px',
    border: `1px solid ${C.buttonBorder}`,
    borderRadius: 4,
    ...fundingType.body,
    color: C.darkText,
    outline: 'none',
    '&::placeholder': { color: C.mutedText },
  },
  browseProgramsList: {
    maxHeight: 280,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    padding: '4px 0',
  },
  browseProgramsOption: {
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    minHeight: 40,
    textAlign: 'left',
    border: 'none',
    background: 'none',
    padding: '10px 16px',
    cursor: 'pointer',
    fontFamily: fundingFont,
    fontSize: 14,
    fontWeight: 400,
    // Extra line-height so Inter glyphs are not clipped by overflow:hidden ellipsis.
    lineHeight: '22px',
    letterSpacing: '0.25px',
    color: C.strongText,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    '&:hover': { backgroundColor: C.metricBg },
  },
  browseProgramsEmpty: {
    margin: 0,
    padding: '16px',
    ...fundingType.body,
    color: C.mutedText,
  },
  eligibilityLabel: {
    fontFamily: fundingFont,
    fontWeight: 500,
    color: C.mutedText,
  },
  viewFundingCard: {
    boxSizing: 'border-box',
    width: '100%',
    backgroundColor: C.cardBg,
    borderRadius: 8,
    padding: '20px 24px',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    fontFamily: fundingFont,
  },
  viewFundingLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 10,
    border: 'none',
    background: 'none',
    padding: 0,
    cursor: 'pointer',
    ...fundingType.bodyMedium,
    color: C.linkBlue,
    textDecoration: 'none',
    textAlign: 'left',
    '&:hover': { textDecoration: 'underline' },
  },
  basicActions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 16,
    width: '100%',
  },
  resetButton: {
    border: 'none',
    background: 'none',
    padding: 0,
    cursor: 'pointer',
    ...fundingType.bodyMedium,
    color: C.strongText,
  },
  saveOutlined: {
    ...fundingPrimaryButton,
    boxSizing: 'border-box',
    height: 40,
    padding: '8px 24px',
    // Labra orange fill with dark label for contrast (match template Save).
    color: C.darkText,
    '&:disabled': {
      backgroundColor: 'rgba(29, 27, 32, 0.12)',
      color: 'rgba(33, 33, 33, 0.38)',
      cursor: 'not-allowed',
      opacity: 1,
    },
  },
  programsCopy: {
    margin: 0,
    ...fundingType.body,
    color: C.mutedText,
  },
  programsTable: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  programsHead: {
    display: 'grid',
    gridTemplateColumns:
      'minmax(0, 3fr) minmax(150px, 0.85fr) minmax(140px, 0.9fr) 36px',
    alignItems: 'center',
    width: '100%',
    ...fundingType.helper,
    color: C.mutedText,
    borderBottom: `1px solid ${C.divider}`,
    '& > span': {
      boxSizing: 'border-box',
      padding: '0 16px 10px',
    },
  },
  programsHeadWithInfo: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    whiteSpace: 'nowrap' as const,
  },
  programsHeadCount: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    whiteSpace: 'nowrap' as const,
  },
  programsHeadAutoSubmit: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  programsRowGroup: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    backgroundColor: C.cardBg,
  },
  programsRow: {
    display: 'grid',
    gridTemplateColumns:
      'minmax(0, 3fr) minmax(150px, 0.85fr) minmax(140px, 0.9fr) 36px',
    alignItems: 'center',
    width: '100%',
    minHeight: 40,
    backgroundColor: 'transparent',
  },
  programCell: {
    boxSizing: 'border-box' as const,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 2,
    minWidth: 0,
    padding: '8px 16px',
  },
  programsRowClickable: {
    cursor: 'pointer',
  },
  programsRowAlt: {
    backgroundColor: C.metricBg,
  },
  /**
   * MPOPP / N/A — title + N/A + toggle on row 1, eligibility on row 2
   * with a tight 2px gap (not a separate block after a tall switch row).
   */
  naProgramBlock: {
    display: 'grid',
    gridTemplateColumns:
      'minmax(0, 3fr) minmax(150px, 0.85fr) minmax(140px, 0.9fr) 36px',
    gridTemplateRows: 'auto auto',
    rowGap: 2,
    alignItems: 'center',
    width: '100%',
    padding: '10px 0',
    boxSizing: 'border-box' as const,
  },
  naProgramTitle: {
    gridColumn: 1,
    gridRow: 1,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    minWidth: 0,
    padding: '0 16px',
    ...fundingType.body,
    color: C.darkText,
  },
  naProgramCount: {
    gridColumn: 2,
    gridRow: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 16px',
    ...fundingType.body,
    letterSpacing: '0.1px',
    color: C.mutedText,
  },
  naProgramToggle: {
    gridColumn: 3,
    gridRow: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 16px',
  },
  programsRowExpand: {
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    padding: '4px 16px 16px',
    maxWidth: '100%',
  },
  /** Figma: template list sits in a bordered white card; Add stays below. */
  templatesCard: {
    boxSizing: 'border-box',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    padding: '12px 16px 12px',
    backgroundColor: C.cardBg,
    border: `1px solid ${C.divider}`,
    borderRadius: 8,
  },
  submissionCriteria: {
    margin: 0,
    ...fundingType.helper,
    color: C.mutedText,
  },
  criteriaMore: {
    padding: 0,
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    fontFamily: fundingFont,
    fontSize: 12,
    fontWeight: 500,
    lineHeight: '16px',
    color: C.linkBlue,
    flexShrink: 0,
  },
  addTemplateLink: {
    alignSelf: 'flex-start',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    marginTop: 4,
    padding: 0,
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    ...fundingType.bodyMedium,
    color: C.linkBlue,
    '& svg': { fontSize: 14 },
  },
  inlineTemplatesTable: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    tableLayout: 'fixed' as const,
    fontFamily: fundingFont,
    '& col.colName': { width: '44%' },
    '& col.colCreated': { width: '36%' },
    '& col.colStatus': { width: '20%' },
    '& th': {
      textAlign: 'left' as const,
      ...fundingType.helper,
      color: C.mutedText,
      padding: '4px 12px 8px 0',
      borderBottom: `1px solid ${C.divider}`,
      fontWeight: 400,
    },
    '& td': {
      padding: '10px 12px 10px 0',
      ...fundingType.body,
      color: C.darkText,
      verticalAlign: 'middle' as const,
      borderBottom: `1px solid ${C.divider}`,
      overflow: 'hidden',
    },
    '& td:last-child': {
      paddingRight: 0,
    },
    '& tbody tr': {
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: C.metricBg,
      },
    },
    '& tr:last-child td': {
      borderBottom: 'none',
    },
  },
  templateNameLink: {
    display: 'block',
    maxWidth: '100%',
    padding: 0,
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    font: 'inherit',
    fontFamily: fundingFont,
    fontWeight: 500,
    color: C.linkBlue,
    textAlign: 'left' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  templateStatusPill: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '2px 8px',
    borderRadius: 999,
    ...fundingType.chip,
  },
  templateStatusActive: {
    backgroundColor: C.successBadgeBg,
    color: C.successText,
  },
  templateStatusDisabled: {
    backgroundColor: C.iconBoxBg,
    color: C.mutedText,
  },
  templateStatusArchived: {
    backgroundColor: C.iconBoxBg,
    color: C.mutedText,
  },
  archivedToggle: {
    alignSelf: 'flex-start',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: 0,
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    ...fundingType.chip,
    color: C.mutedText,
    '& svg': { fontSize: 12 },
  },
  programName: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    minWidth: 0,
    ...fundingType.body,
    color: C.darkText,
  },
  /** N/A eligibility — spans full card width, 2px under the title. */
  programEligibilityRow: {
    boxSizing: 'border-box' as const,
    gridColumn: '1 / -1',
    gridRow: 2,
    display: 'flex',
    alignItems: 'baseline',
    gap: 4,
    width: '100%',
    padding: '0 16px',
    minWidth: 0,
  },
  programEligibility: {
    margin: 0,
    ...fundingType.helper,
    color: C.mutedText,
    flex: '1 1 auto',
    minWidth: 0,
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  programEligibilityExpanded: {
    whiteSpace: 'normal' as const,
    overflow: 'visible',
    textOverflow: 'clip',
  },
  templateCount: {
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '10px 16px',
    ...fundingType.body,
    letterSpacing: '0.1px',
    color: C.mutedText,
  },
  autoSubmitCell: {
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '10px 16px',
  },
  /**
   * Auto-submit on/off — black thumb for both off and on (not MUI white when off).
   */
  autoSubmitSwitch: {
    '& .MuiSwitch-switchBase': {
      color: C.strongText,
      '&.Mui-checked': {
        color: C.strongText,
        '& + .MuiSwitch-track': {
          backgroundColor: C.strongText,
          opacity: 0.35,
        },
      },
      '&.Mui-disabled': {
        color: C.strongText,
        opacity: 0.38,
      },
    },
    '& .MuiSwitch-thumb': {
      backgroundColor: C.strongText,
      boxShadow:
        '0px 2.5px 3.75px 0px rgba(0,0,0,0.1), 0px 6.25px 9.375px 0px rgba(0,0,0,0.1)',
    },
    '& .MuiSwitch-track': {
      backgroundColor: '#e6e8e9',
      opacity: 1,
      border: `1px solid ${C.divider}`,
    },
    '& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track': {
      opacity: 0.5,
    },
  },
  expandCell: {
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '10px 8px',
  },
  iconButton: {
    width: 24,
    height: 24,
    padding: 0,
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: C.mutedText,
    '& svg': { fontSize: 16 },
  },
  rowInfoIcon: {
    fontSize: 14,
    color: C.mutedText,
  },
  sideCard: {
    boxSizing: 'border-box',
    width: '100%',
    backgroundColor: C.cardBg,
    borderRadius: 8,
    padding: 32,
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
  },
  sideCardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    width: '100%',
  },
  sideCardTitle: {
    ...fundingType.sectionTitle,
    color: C.strongText,
    margin: 0,
  },
  auditEmpty: {
    flex: '1 1 auto',
    minHeight: 150,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  auditWatermark: {
    width: 112.921,
    height: 87,
    opacity: 0.18,
  },
  auditScrollWrap: {
    position: 'relative',
    width: '100%',
  },
  auditList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    width: '100%',
    maxHeight: 190,
    overflowY: 'auto',
  },
  auditItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    width: '100%',
  },
  auditItemText: {
    ...fundingType.body,
    letterSpacing: '0.1px',
    color: C.darkText,
  },
  auditItemTime: {
    ...fundingType.helper,
    color: C.mutedText,
  },
  auditDivider: {
    display: 'block',
    width: '100%',
    height: 1,
    backgroundColor: C.divider,
  },
  /** Shimmer bar used while Basic info / programs table fetch. */
  skeletonBar: {
    display: 'block',
    borderRadius: 4,
    background: `linear-gradient(
      90deg,
      ${C.metricBg} 0%,
      ${C.divider} 45%,
      ${C.metricBg} 90%
    )`,
    backgroundSize: '200% 100%',
    animation: `${skeletonShimmer} 1.35s ease-in-out infinite`,
  },
  skeletonField: {
    height: FUNDING_FORM_FIELD_HEIGHT,
    width: '100%',
  },
  skeletonHelper: {
    height: 12,
    width: '55%',
    marginTop: 6,
  },
  skeletonProgramsRow: {
    display: 'grid',
    gridTemplateColumns:
      'minmax(0, 3fr) minmax(100px, 0.7fr) minmax(120px, 0.9fr) 36px',
    alignItems: 'center',
    width: '100%',
    minHeight: 44,
    padding: '0 16px',
    boxSizing: 'border-box' as const,
    gap: 8,
  },
  skeletonProgramsName: {
    height: 14,
    width: '72%',
  },
  skeletonProgramsCount: {
    height: 14,
    width: 28,
  },
  skeletonProgramsToggle: {
    height: 20,
    width: 36,
    borderRadius: 10,
  },
  skeletonProgramsChevron: {
    height: 12,
    width: 12,
    justifySelf: 'center' as const,
  },
}))
